import { observer } from 'mobx-react-lite'
import { runInAction } from 'mobx'
import { CheckIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { RefObject } from 'react'
import { CropperRef, DrawOptions } from 'react-advanced-cropper'

import { Button } from './Button'
import { EditContext } from './types'
import { Slider } from './Slider'
import { modes, setModeDefaults } from './modes'

export const Toolbar = observer(
    ({
        cropperRef,
        context,
        onCancel,
        onSubmit,
    }: {
        cropperRef: RefObject<CropperRef>
        context: EditContext
        onCancel: () => void
        onSubmit: (image: Blob) => void
    }) => {
        const { mode } = context

        return (
            <div className="flex shrink-0 items-center pt-4">
                <div className="flex shrink-0 items-center gap-x-0.5">
                    <Button active={mode === 'crop'} onClick={() => runInAction(() => (context.mode = 'crop'))}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M18.2 15.2h-1.5V7.3H8.8V5.8c0-.4-.3-.8-.8-.8s-.7.4-.7.8v1.5H5.8c-.4 0-.8.3-.8.8s.3.8.8.8h1.5v7.8h7.8v1.5c0 .4.3.8.8.8s.8-.3.8-.8v-1.5h1.5c.4 0 .8-.3.8-.8s-.4-.7-.8-.7zM8.8 8.8h6.3v6.3H8.8V8.8z" />
                        </svg>
                    </Button>
                    <Button
                        active={mode === 'saturation'}
                        onClick={() => runInAction(() => (context.mode = 'saturation'))}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M14.2 16c.5-.5.8-1.2.8-2 0-.3 0-.6-.1-.9-.3.1-.5.1-.8.1-1.3 0-2.2-.7-2.9-1.3-.2-.2-.8-.6-.9-.6 0 0-.1 0-.3.1-.5 1-.8 1.8-.8 2.5s.3 1.4.8 2c1 1.3 3 1.3 4.2.1z" />
                            <path d="M12 19.7c-1.5 0-3-.6-4.1-1.7-1.1-1.1-1.6-2.5-1.6-4 .1-2.7 1.8-5 3.6-7.4.5-.6 1-1.3 1.4-2l.7-.8.6.9c.5.7 1 1.4 1.5 2 1.8 2.4 3.5 4.6 3.6 7.3 0 1.5-.5 2.9-1.6 4-1.1 1-2.6 1.7-4.1 1.7zm0-13.3-.9 1.2C9.5 9.7 7.9 11.8 7.8 14c0 1.1.4 2.1 1.1 2.9.8.8 1.9 1.3 3 1.3s2.3-.5 3-1.3c.8-.8 1.2-1.8 1.1-2.9-.1-2.1-1.5-4.1-3.3-6.4-.1-.4-.4-.8-.7-1.2z" />
                        </svg>
                    </Button>
                    <Button active={mode === 'contrast'} onClick={() => runInAction(() => (context.mode = 'contrast'))}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M12 4.4c-4.2 0-7.6 3.4-7.6 7.6s3.4 7.6 7.6 7.6 7.6-3.4 7.6-7.6-3.4-7.6-7.6-7.6zM5.9 12c0-3.3 2.7-6.1 6.1-6.1V18c-3.3.1-6.1-2.7-6.1-6z" />
                        </svg>
                    </Button>
                    <Button
                        active={mode === 'brightness'}
                        onClick={() => runInAction(() => (context.mode = 'brightness'))}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M12 15.3c-1.8 0-3.3-1.5-3.3-3.3 0-1.8 1.5-3.3 3.3-3.3 1.8 0 3.3 1.5 3.3 3.3 0 1.8-1.5 3.3-3.3 3.3zm0-5.1c-1 0-1.8.8-1.8 1.8s.8 1.8 1.8 1.8 1.8-.8 1.8-1.8-.8-1.8-1.8-1.8zM12 7.7c-.4 0-.8-.3-.8-.8V5.2c0-.4.3-.8.8-.8s.8.3.8.8V7c0 .4-.4.7-.8.7zM9 9c-.3.3-.8.3-1.1 0L6.6 7.7c-.3-.3-.3-.8 0-1.1.3-.3.8-.3 1.1 0L9 7.9c.3.3.3.8 0 1.1zM7.7 12c0 .4-.3.8-.8.8H5.2c-.4 0-.8-.3-.8-.8s.3-.8.8-.8H7c.4.1.7.4.7.8zM9 15c.3.3.3.8 0 1.1l-1.3 1.3c-.3.3-.8.3-1.1 0-.3-.3-.3-.8 0-1.1L7.9 15c.3-.3.8-.3 1.1 0zM12 16.3c.4 0 .8.3.8.8v1.8c0 .4-.3.8-.8.8s-.8-.3-.8-.8V17c.1-.4.4-.7.8-.7zM15 15c.3-.3.8-.3 1.1 0l1.3 1.3c.3.3.3.8 0 1.1s-.8.3-1.1 0L15 16.1c-.3-.3-.3-.8 0-1.1zM16.3 12c0-.4.3-.8.8-.8h1.8c.4 0 .8.3.8.8s-.3.8-.8.8H17c-.4 0-.7-.4-.7-.8zM15 9c-.3-.3-.3-.8 0-1.1l1.3-1.3c.3-.3.8-.3 1.1 0 .3.3.3.8 0 1.1L16.1 9c-.3.3-.8.3-1.1 0z" />
                        </svg>
                    </Button>
                    <Button active={mode === 'hue'} onClick={() => runInAction(() => (context.mode = 'hue'))}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M12 19.6c-4.2 0-7.6-3.4-7.6-7.6S7.8 4.4 12 4.4s7.6 3.4 7.6 7.6-3.4 7.6-7.6 7.6zm0-13.7c-3.3 0-6.1 2.7-6.1 6.1s2.7 6.1 6.1 6.1 6.1-2.7 6.1-6.1-2.8-6.1-6.1-6.1z" />
                            <path d="M12 15.3c-1.8 0-3.3-1.5-3.3-3.3 0-1.8 1.5-3.3 3.3-3.3 1.8 0 3.3 1.5 3.3 3.3 0 1.8-1.5 3.3-3.3 3.3zm0-5.1c-1 0-1.8.8-1.8 1.8s.8 1.8 1.8 1.8 1.8-.8 1.8-1.8-.8-1.8-1.8-1.8z" />
                        </svg>
                    </Button>
                    <Button
                        onClick={() =>
                            runInAction(() => {
                                if (cropperRef.current) {
                                    cropperRef.current.reset()
                                }
                                setModeDefaults(context)
                            })
                        }
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            onClick={() => runInAction(() => setModeDefaults(context))}
                        >
                            <path d="M21.2 9c-.3-.2-.8-.1-1 .2l-.8 1.3c-.7-3.4-3.8-6-7.4-6-4.2 0-7.6 3.4-7.6 7.6s3.4 7.6 7.6 7.6c2.3 0 4.5-1.1 6-2.9.3-.3.2-.8-.1-1.1-.3-.3-.8-.2-1.1.1-1.2 1.5-2.9 2.3-4.8 2.3-3.3 0-6.1-2.7-6.1-6.1S8.7 5.9 12 5.9c2.9 0 5.4 2.1 5.9 4.9l-1.3-.8c-.4-.2-.8-.1-1 .3-.2.4-.1.8.3 1l2.9 1.6c.1.1.2.1.4.1.3 0 .5-.1.6-.4l1.7-2.8c.2-.1.1-.6-.3-.8z" />
                        </svg>
                    </Button>
                </div>
                <div className="ml-6 flex flex-1 items-center">
                    <Slider
                        // @ts-expect-error not sure how to fix this
                        value={context[modes[mode].property]}
                        min={modes[mode].min}
                        max={modes[mode].max}
                        step={modes[mode].step}
                        onChange={(evt) => {
                            const value = evt.currentTarget.valueAsNumber

                            runInAction(() => {
                                // @ts-expect-error not sure how to fix this
                                context[modes[mode].property] = value
                            })
                        }}
                    />
                </div>
                <div className="ml-8 flex shrink-0 items-center gap-x-0.5">
                    <Button onClick={onCancel}>
                        <XMarkIcon className="text-error-500" />
                    </Button>
                    <Button
                        onClick={() => {
                            if (cropperRef.current) {
                                let options: DrawOptions | undefined

                                if (context.config.image.maxSize) {
                                    options = {
                                        maxHeight: context.config.image.maxSize,
                                        maxWidth: context.config.image.maxSize,
                                    }
                                }

                                const canvas = cropperRef.current.getCanvas(options)

                                if (canvas) {
                                    canvas.toBlob(
                                        (data) => {
                                            if (data) {
                                                onSubmit(data)
                                            }
                                        },
                                        `image/${context.config.image.format}`,
                                        context.config.image.compress ? 0.8 : 1
                                    )
                                }
                            }
                        }}
                    >
                        <CheckIcon className="text-green-500" />
                    </Button>
                </div>
            </div>
        )
    }
)
